import { useQuery } from "@tanstack/react-query"
import { createContext, useContext, useMemo, useState, type ReactNode } from "react"
import { api } from "../api/client"
import type { FamilySummary, Paged } from "../api/types"

interface FamilyContextValue {
  families: FamilySummary[]
  activeFamily: FamilySummary | null
  activeFamilyId: string | null
  setActiveFamilyId(id: string): void
  loading: boolean
  error: Error | null
  refetch(): Promise<unknown>
}

const FamilyContext = createContext<FamilyContextValue | null>(null)
const STORAGE_KEY = "family-coin.active-family"

export function FamilyProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState(() => localStorage.getItem(STORAGE_KEY))
  const query = useQuery({
    queryKey: ["families"],
    queryFn: () => api.get<Paged<FamilySummary>>("/families"),
    staleTime: 60_000
  })
  const families = useMemo(() => query.data?.items ?? [], [query.data])
  const activeFamily = useMemo(
    () => families.find((family) => family.id === selected) ?? families[0] ?? null,
    [families, selected]
  )

  const setActiveFamilyId = (id: string) => {
    setSelected(id)
    localStorage.setItem(STORAGE_KEY, id)
  }

  const value = useMemo<FamilyContextValue>(
    () => ({
      families,
      activeFamily,
      activeFamilyId: activeFamily?.status === "SUSPENDED" ? null : (activeFamily?.id ?? null),
      setActiveFamilyId,
      loading: query.isLoading,
      error: query.error,
      refetch: query.refetch
    }),
    [activeFamily, families, query.error, query.isLoading, query.refetch]
  )

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>
}

export function useFamily() {
  const value = useContext(FamilyContext)
  if (!value) throw new Error("useFamily must be used inside FamilyProvider")
  return value
}
